// Third party
import React, { useState } from 'react';
import { connect } from 'react-redux';
import { RouteComponentProps, Link } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';

// Utils
import ZendeskUtils from '@/utils/3rd-party/zendesk';
import { userHasPermission, PERMISSIONS, userHasPermissionEnabled } from '@/utils/permissions';
import { formatMessage } from '@/utils/i18n/i18n';
import notification from '@/utils/notification';
import { LOCALE_OPTIONS as localeOptions } from '@/utils/i18n';

// Types
import { ReduxState } from '@/redux/modules/types';
import UserPreferences from '@/types/user-preferences';

// Components
import { Dropdown } from 'react-bootstrap';
import { InviteConfirmationModal, InvitePeopleModal, SwitchAccessModal } from '../modals';
import defaultAvatar from '../../../images/user-icon.svg';

// Actions
import { logOut, updatePreferences } from '@/redux/modules/auth';
import { setLocale } from '../../../redux/modules/intl';
import { setSelectedDelegateAccess } from '@/redux/modules/delegates';
import { inviteUsers } from '../../../redux/modules/auth';
import Locale from '@/types/locale';
import User from '@/types/user';
import AppMode from '@/types/app-mode';
import { NAV_PATHS } from '@/utils/constants';
import { Avatar } from '@capintel/component-lib/avatar';

type DispatchProps = {
  logOut: () => Promise<string | null>;
  inviteUsers: (emails: string[]) => Promise<void>;
  setSelectedDelegateAccess: (id: string) => Promise<void>;
  setLocale: (locale: Locale) => void;
  updatePreferences: (preferences: Partial<UserPreferences>) => Promise<void>;
};

type StateProps = {
  locale: Locale;
};

type Props = DispatchProps &
  StateProps &
  RouteComponentProps & {
    user: User;
  };

const UserDropdown = ({ user, logOut, locale, setLocale, inviteUsers, setSelectedDelegateAccess, history }: Props) => {
  const handleLogout = () => {
    logOut().then(path => {
      ZendeskUtils.logout();
      if (path) {
        window.location.href = path;
      } else {
        history.replace('/auth/login');
      }
    });
  };

  // We only need the locales the user has access to and the one not currently set
  const filteredLocales = localeOptions.filter(localeOption => user.appConfig.appLocales.includes(localeOption.value) && localeOption.value !== locale);

  const toggleLang = (locale: Locale) => {
    setLocale(locale);
    updatePreferences({ locale });
    ZendeskUtils.setLocale(locale);

    // Use setTimeout to make sure language has been changed before sending the notification.
    setTimeout(() => {
      notification.success(formatMessage('Settings.Preferences.LanguageChanged'));
    }, 0);
  };

  const [showSwitchAccessModal, setShowSwitchAccessModal] = useState(false);
  const [showInvitesModal, setShowInvitesModal] = useState(false);
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);

  const switchAccess = (id: string) => {
    setShowSwitchAccessModal(false);

    // Selected user is the current user - nothing to do
    if (!user || id === user.id) {
      return;
    }

    setSelectedDelegateAccess(id).then(() => {
      window.location.replace(NAV_PATHS.COMPARISONS);
    });
  };

  const reOpenInvites = () => {
    setShowConfirmationModal(false);
    setShowInvitesModal(true);
  };

  const inviteSuccess = () => {
    setShowConfirmationModal(true);
    setShowInvitesModal(false);
  };

  const getUserMenuOptions = (user: User) => {
    const { authUser } = user;
    const avatarUrl = (user && user.imgUrl) || defaultAvatar;

    const options = [
      {
        element: (
          <div className="static-userName dropdown-item">
            <div className="img-container">
              <img src={avatarUrl} alt="" />
            </div>
            <div className="user-info">
              <span className="user-name">
                {authUser.fname} {authUser.lname}
              </span>
              <span>{authUser.email}</span>
            </div>
          </div>
        ),
        hidden: false,
      },
      {
        element: (
          <Dropdown.Item role="menuitem" as={Link} to="/settings" tabIndex={0} data-testid="user-dropdown-settings">
            <i className="icon-settings" />
            <FormattedMessage id="SiteNavigation.Settings" />
          </Dropdown.Item>
        ),
        hidden: !userHasPermissionEnabled(user, PERMISSIONS.SECTION_SETTINGS),
      },
      {
        element: (
          <Dropdown.Item role="menuitem" as="button" onClick={() => setShowInvitesModal(true)}>
            <i className="icon-invite" />
            <FormattedMessage id="SiteNavigation.InvitePeople" />
          </Dropdown.Item>
        ),
        hidden: !userHasPermission(user, PERMISSIONS.INVITE_PEOPLE),
      },
      {
        element: (
          <Dropdown.Item role="menuitem" as={Link} to="/announcements" tabIndex={0}>
            <i className="icon-announcement-new" />
            <FormattedMessage id="SiteNavigation.Announcements" />
          </Dropdown.Item>
        ),
        hidden: !userHasPermission(user, PERMISSIONS.ENTERPRISE_ADMIN_NOTIFICATION_CENTER),
      },
      {
        element: (
          <Dropdown.Item role="menuitem" onClick={() => setShowSwitchAccessModal(true)}>
            <i className="icon-switch-user" />
            <FormattedMessage id="Delegates.SwitchAccount" />
          </Dropdown.Item>
        ),
        hidden: authUser.delegateAccess?.myAccesses == null || authUser.delegateAccess?.myAccesses.length === 0,
      },
    ];

    // Append the language toggle options
    filteredLocales.forEach(localeOption => {
      options.push({
        element: (
          <Dropdown.Item role="menuitem" as="button" onClick={() => toggleLang(localeOption.value)}>
            <i className="icon-globe" />
            <FormattedMessage id={localeOption.label} />
          </Dropdown.Item>
        ),
        hidden: user.appConfig.mode !== AppMode.LINK || !filteredLocales.length,
      });
    });

    // Finally, we add the logout option at the very end
    options.push({
      element: (
        <Dropdown.Item role="menuitem" as="button" onClick={handleLogout}>
          <i className="icon-signout" />
          <FormattedMessage id="SiteNavigation.LogOut" />
        </Dropdown.Item>
      ),
      hidden: false,
    });

    return options.filter(opt => !opt.hidden).map(opt => opt.element);
  };

  return (
    <>
      <Dropdown className="user-dropdown-menu">
        <Dropdown.Toggle id="user-dropdown-menu" data-testid="user-dropdown-menu" aria-label={formatMessage('SiteNavigation.UserPreferences')}>
          <Avatar image={(user && user.imgUrl) || defaultAvatar} />
        </Dropdown.Toggle>
        <Dropdown.Menu as="ul" role="menu" align="end">
          {getUserMenuOptions(user).map((option, index) => (
            <li key={`${option.key}-${index}`} role="presentation">
              {option}
            </li>
          ))}
        </Dropdown.Menu>
      </Dropdown>

      <SwitchAccessModal show={showSwitchAccessModal} onCancel={() => setShowSwitchAccessModal(false)} onConfirm={switchAccess} user={user} />

      <InvitePeopleModal onSuccess={inviteSuccess} showModal={showInvitesModal} onSubmit={inviteUsers} onCancel={() => setShowInvitesModal(false)} />
      <InviteConfirmationModal showModal={showConfirmationModal} onConfirm={() => reOpenInvites()} onCancel={() => setShowConfirmationModal(false)} />
    </>
  );
};

const mapStateToProps = (state: ReduxState): StateProps => ({
  locale: state.intl.locale,
});

export default connect(mapStateToProps, {
  logOut,
  setLocale,
  updatePreferences,
  inviteUsers,
  setSelectedDelegateAccess,
})(UserDropdown);
